import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { defaultFiltersByFolders } from "../../../../Utils/utils"
import { REQUESTS_RECEIPTS_SLICE_NAME as SLICE_NAME } from "./requestsReceipts.constants"
import {
  deleteRequestsReceipt,
  getReceiptProfileUpdateDiff,
  getReceiptRequestDetails,
  getRequestsReceipts,
  getRequestsReceiptsExport,
  getRequestsReceiptsFilterOptions,
  updateRequestsReceipt
} from "./requestsReceipts.thunks"

/* ------------- Initial State ------------- */

export const pinnedCols = ["Status"]

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "updated_timestamp", label: "Update status date", active: true },
  { field_id: "is_use_another_solutions", label: "Using Competitor", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "enrollments", label: "Enrollments", active: true },
  { field_id: "programs", label: "Programs", active: true },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: true },
  { field_id: "visa_arn_code_date", label: "ARN Added Date", active: true },
  { field_id: "arn_codes", label: "ARN codes", active: true },
  { field_id: "bin_codes", label: "BIN codes", active: false },
  { field_id: "caid_codes", label: "CAID codes", active: false },
  { field_id: "balance_current", label: "Current Balance", active: true },
  // Only meaningful when Using Competitor is Yes (spec BLOCK 4, columns 15-17), so they ship
  // hidden by default just as the same three do on the Enrollments table.
  { field_id: "another_solution_name", label: "Enrollment details", active: false },
  { field_id: "previously_connected_enrollments", label: "Previously enrolled", active: false },
  {
    field_id: "is_user_notified_previous_solution",
    label: "Previous partner notified",
    active: false
  },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "business_url", label: "Business URL", active: true },
  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "request_status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_status: "all",
    created_timestamp: null,
    updated_timestamp: null,
    visa_arn_code_date: null,
    is_use_another_solutions: "all",
    enrollments: [],
    programs: [],
    merchant_descriptor: "all",
    arn_codes: [],
    bin_codes: [],
    caid_codes: [],
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],
    exclude_fields: [],
    is_archived: null,
    admin_comment: [],
    admin_screenshots: "all",
    balance_current: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptionsRequestId: null,
  filterOptions: {
    request_status: [],
    admin_comment: []
  },

  visibleFiltersAdmin: {
    request_status: true,
    user_email: true,
    user_full_name: true,
    business_account: true
  },

  exportFetching: false,
  exportResult: null,

  details: {
    fetching: false,
    data: null,
    error: null
  },
  profileUpdate: {
    fetching: false,
    data: null,
    error: null
  }
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp(state, { payload: { key, value } }) {
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = Date.now()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearFilters(state, { payload: data }) {
      state.filters = { ...initialState.filters, ...(data || {}) }
    },
    resetDetails(state) {
      state.details = { fetching: false, data: null, error: null }
    },
    resetProfileUpdate(state) {
      state.profileUpdate = { fetching: false, data: null, error: null }
    },
    logout(state) {
      const { filters, columnsAdmin, visibleFiltersAdmin } = state
      Object.assign(state, initialState)
      state.filters = filters
      state.columnsAdmin = columnsAdmin
      state.visibleFiltersAdmin = visibleFiltersAdmin
    }
  },
  extraReducers: (builder) => {
    builder
      // getRequestsReceiptsFilterOptions
      .addCase(getRequestsReceiptsFilterOptions.pending, (state, action) => {
        state.fetchingFilterOptions = true
        state.filterOptionsRequestId = action.meta.requestId
      })
      .addCase(getRequestsReceiptsFilterOptions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.filterOptionsRequestId) return

        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getRequestsReceiptsFilterOptions.rejected, (state, action) => {
        if (action.meta.requestId !== state.filterOptionsRequestId) return

        state.fetchingFilterOptions = false
      })

      // getRequestsReceipts
      .addCase(getRequestsReceipts.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsReceipts.fulfilled, (state, { payload: result }) => {
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = { count: result?.length || 0, results: result || [] }
        }
      })
      .addCase(getRequestsReceipts.rejected, (state) => {
        state.fetching = false
      })

      // updateRequestsReceipt
      .addCase(updateRequestsReceipt.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateRequestsReceipt.fulfilled, (state, { payload: receipt }) => {
        state.fetching = false
        // Local patch of `request_status` only — same pattern as RequestsEnrollments.
        // `is_archived` isn't patched here: the caller refetches after archive /
        // unarchive so the row leaves/enters the sub-tab it no longer/now matches.
        state.result.results = state.result.results?.map((el) =>
          el.id === receipt.id ? { ...el, request_status: receipt.request_status } : el
        )
      })
      .addCase(updateRequestsReceipt.rejected, (state) => {
        state.fetching = false
      })

      // deleteRequestsReceipt
      .addCase(deleteRequestsReceipt.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteRequestsReceipt.fulfilled, (state, { payload: { id } }) => {
        state.fetching = false
        state.result.results = state.result.results?.filter((el) => el.id !== id)
        state.result.count = Math.max(0, (state.result.count || 0) - 1)
      })
      .addCase(deleteRequestsReceipt.rejected, (state) => {
        state.fetching = false
      })

      // getRequestsReceiptsExport
      .addCase(getRequestsReceiptsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getRequestsReceiptsExport.fulfilled, (state, { payload }) => {
        state.exportFetching = false
        state.exportResult = payload
      })
      .addCase(getRequestsReceiptsExport.rejected, (state) => {
        state.exportFetching = false
      })

      // getReceiptRequestDetails — Eye → Profile details modal (§6.1)
      .addCase(getReceiptRequestDetails.pending, (state) => {
        state.details.fetching = true
        state.details.data = null
        state.details.error = null
      })
      .addCase(getReceiptRequestDetails.fulfilled, (state, { payload }) => {
        state.details.fetching = false
        state.details.data = payload
      })
      .addCase(getReceiptRequestDetails.rejected, (state, { payload }) => {
        state.details.fetching = false
        state.details.error = payload || true
      })

      .addCase(getReceiptProfileUpdateDiff.pending, (state) => {
        state.profileUpdate.fetching = true
        state.profileUpdate.data = null
        state.profileUpdate.error = null
      })
      .addCase(getReceiptProfileUpdateDiff.fulfilled, (state, { payload }) => {
        state.profileUpdate.fetching = false
        state.profileUpdate.data = payload
      })
      .addCase(getReceiptProfileUpdateDiff.rejected, (state, { payload }) => {
        state.profileUpdate.fetching = false
        state.profileUpdate.error = payload || true
      })
  }
})

export const { setProp, clearFilters, resetDetails, resetProfileUpdate, logout } = slice.actions

/* ------------- Selectors ------------- */

export const selectDetails = (state) => state.requestsReceipts.details

export const selectProfileUpdate = (state) => state.requestsReceipts.profileUpdate

export const selectAreFiltersDefault = (state, folder) => {
  const defaultFilters = defaultFiltersByFolders.receipts?.[folder] || initialState.filters
  const filtersToCompare = {
    ...initialState.filters,
    request_status: defaultFilters.request_status,
    is_archived: defaultFilters.is_archived
  }
  return isEqual(filtersToCompare, state.requestsReceipts.filters)
}

/* ------------- Persistence ------------- */

// Bumped when the column registry changes: the migration below rebuilds persisted state from the
// current defaults, so an admin who already opened the page still receives newly added columns.
const PERSISTED_REDUCER_VERSION = 3

export const persistedReducer = persistReducer(
  {
    key: "requestsReceiptsThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
